/**
 * Wat het bord moet laten oplichten bij een openstaande vraag.
 *
 * Dit bestandje bestaat om één reden: hier zaten twee vastlopers. In `highlightsVoor` stond
 * een vangnet dat voor élke vraag die het niet kende de landschappen liet oplichten. Bij de
 * Bouwmeester ging je klik dan als bordkolom naar de regelkern, die er een stapelindex van
 * maakte — vandaar de "willekeurige kaart". Bij de Burgeroorlog was het dubbel mis: je eigen
 * vorstendom lichtte op terwijl de kern een ridder van de ánder verwachtte, en antwoordde
 * dus terecht "daar staat geen ridder of vloot". De speler kon de bedoelde keuze nooit
 * maken, en het potje stond stil.
 *
 * De beslissing staat hier los van pixi en van de schermtoestand, zodat een toets over
 * **alle** vragen kan lopen. De `never`-tak onderaan laat de compiler meebewaken; de toets
 * vangt wat de compiler niet ziet — bijvoorbeeld iemand die er later een `default:`
 * overheen legt.
 */
import type { Seat } from '@catan/contracts';
import type { Pending } from '@catan/rules';

export type Markering =
  /** Het bord doet niets: een overlay, de handkaarten of de stapels nemen het over. */
  | { readonly soort: 'geen' }
  /** Wijs een eigen landschap aan — waar de grondstof heen gaat. */
  | { readonly soort: 'eigen-landschappen' }
  /** Pak iets weg bij deze speler; lege landschappen doen niet mee. */
  | { readonly soort: 'landschappen-van'; readonly seat: Seat }
  /** Wijs een gebouwde ridder of vloot aan bij deze speler. */
  | { readonly soort: 'gebouwd-van'; readonly seat: Seat }
  /** Kies boven of onder in deze buitenkolom voor het net getrokken land. */
  | { readonly soort: 'lege-plekken'; readonly column: number };

/**
 * De vragen die géén markering op het bord krijgen.
 *
 * Ze wijzen een kaart, een stapel of een knop aan. Ze staan hier als aparte lijst zodat de
 * `switch` hieronder leesbaar blijft — en zodat je in één oogopslag ziet wélke vragen op een
 * eigen scherm leunen.
 */
const EIGEN_SCHERM = [
  'kies-afleggen',
  'kies-afleg-stapel',
  'kies-uit-stapel',
  'kies-startkaarten',
  'kies-tegenkaart',
  'verkenner-vraag',
  'verkenner-kies',
  'spion',
  'kies-handkaarten',
  'bouwmeester',
] as const satisfies readonly Pending['kind'][];

export function markeringVoor(pending: Pending): Markering {
  switch (pending.kind) {
    case 'kies-afleggen':
    case 'kies-afleg-stapel':
    case 'kies-uit-stapel':
    case 'kies-startkaarten':
    case 'kies-tegenkaart':
    case 'verkenner-vraag':
    case 'verkenner-kies':
    case 'spion':
    case 'kies-handkaarten':
    case 'bouwmeester':
      return { soort: 'geen' };

    case 'kies-terugname':
      return { soort: 'gebouwd-van', seat: pending.from };

    // De Karavaan wijst in beide stappen eigen tegels aan: eerst wat je afgeeft, dan
    // waar de nieuwe grondstoffen komen. De teruggave van de Handelaar net zo: je wijst
    // je éígen gevulde tegel aan; wat er niet naar de ander kan, weigert de kern netjes.
    case 'kies-opbrengst':
    case 'plaats-buit':
    case 'karavaan':
    case 'handelaar-terug':
      return { soort: 'eigen-landschappen' };

    case 'kies-buit':
    case 'handelaar':
      return { soort: 'landschappen-van', seat: pending.from };

    case 'leg-landschap':
      return { soort: 'lege-plekken', column: pending.column };
  }

  // Onbereikbaar zolang elke variant hierboven staat — de compiler bewaakt dat. Blijft er
  // ooit toch iets over, dan is géén markering eerlijker dan landschappen die naar de
  // verkeerde vraag verwijzen: een speler die niets ziet oplichten vraagt zich af wat er
  // moet gebeuren, een speler die het verkéérde ziet oplichten klikt zich vast.
  const onbekend: never = pending;
  return onbekend;
}

/** Alleen voor toetsen: de vragen waarvan we weten dat ze een eigen scherm hebben. */
export const VRAGEN_MET_EIGEN_SCHERM: readonly Pending['kind'][] = EIGEN_SCHERM;
